// ============================================
// Therapist Active Session
// ============================================

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Session, Event } from '../../types';

export default function TherapistSession() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = React.useState<Session | null>(null);
  const [event, setEvent] = React.useState<Event | null>(null);
  const [eventId, setEventId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [timeRemaining, setTimeRemaining] = React.useState(0);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [showComplete, setShowComplete] = React.useState(false);
  const [amountReceived, setAmountReceived] = React.useState('');
  const [completing, setCompleting] = React.useState(false);
  const [actualMinutes, setActualMinutes] = React.useState(0);
  const [actualBillAmount, setActualBillAmount] = React.useState(0);
  const [sessionFrozen, setSessionFrozen] = React.useState(false);
  const [pausing, setPausing] = React.useState(false);
  const [resuming, setResuming] = React.useState(false);

  const [notFound, setNotFound] = React.useState(false);
  const [searchTimeout, setSearchTimeout] = React.useState(false);
  const [debugInfo, setDebugInfo] = React.useState<string>('');
  const [showReportForm, setShowReportForm] = React.useState(false);
  const [reportComment, setReportComment] = React.useState('');
  const [reportSubmitting, setReportSubmitting] = React.useState(false);
  const [reportSubmitted, setReportSubmitted] = React.useState(false);

  // Find session and event
  React.useEffect(() => {
    if (!sessionId || !user) return;

    console.log('[Session] Starting search for session:', sessionId);
    console.log('[Session] User UID:', user.uid);
    setDebugInfo(`Searching for session ${sessionId}...`);

    // Set a timeout - if we can't find the session within 10 seconds, show error
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.error('[Session] TIMEOUT: Could not find session within 10 seconds');
        console.error('[Session] Session ID:', sessionId);
        console.error('[Session] User UID:', user.uid);
        setSearchTimeout(true);
        setLoading(false);
        setDebugInfo(`Timeout searching for session ${sessionId}`);
      }
    }, 10000);

    // We need to find which event this session belongs to
    // For now, search through events (in production, you'd store eventId in URL or context)
    const findSession = async () => {
      try {
        const { collection, getDocs, query, where } = await import('firebase/firestore');
        // Only query live events (therapists can only read live events)
        const eventsQuery = query(collection(db, 'events'), where('status', '==', 'live'));
        const eventsSnap = await getDocs(eventsQuery);

        console.log('[Session] Found', eventsSnap.docs.length, 'live events to search');
        setDebugInfo(`Found ${eventsSnap.docs.length} live events...`);

        for (const eventDoc of eventsSnap.docs) {
          console.log('[Session] Checking event:', eventDoc.id);

          const sessionDoc = await new Promise<any>((resolve) => {
            const unsub = onSnapshot(
              doc(db, 'events', eventDoc.id, 'sessions', sessionId),
              (snap) => {
                unsub();
                resolve(snap);
              },
              (error) => {
                console.error('[Session] Error reading session from event', eventDoc.id, ':', error);
                unsub();
                resolve({ exists: () => false });
              }
            );
          });

          if (sessionDoc.exists()) {
            const sessionData = sessionDoc.data();
            console.log('[Session] FOUND session in event:', eventDoc.id);
            console.log('[Session] Session status:', sessionData.status);
            console.log('[Session] Session therapistId:', sessionData.therapistId);
            console.log('[Session] Current user:', user.uid);

            clearTimeout(timeoutId);
            setEventId(eventDoc.id);
            setSession({ id: sessionDoc.id, ...sessionData } as Session);
            setEvent({ id: eventDoc.id, ...eventDoc.data() } as Event);
            setLoading(false);

            // Now subscribe to real-time updates
            const unsub = onSnapshot(
              doc(db, 'events', eventDoc.id, 'sessions', sessionId),
              (snap) => {
                if (snap.exists()) {
                  const data = { id: snap.id, ...snap.data() } as Session;
                  setSession(data);

                  // If session completed, redirect back
                  if (data.status === 'completed') {
                    console.log('[Session] Session completed, redirecting to home');
                    navigate('/t');
                  }
                }
              }
            );

            return unsub;
          }
        }

        // Session not found in any live event
        console.error('[Session] NOT FOUND: Session not found in any live event');
        console.error('[Session] Session ID:', sessionId);
        console.error('[Session] Events searched:', eventsSnap.docs.map(d => d.id));
        clearTimeout(timeoutId);
        setNotFound(true);
        setLoading(false);
        setDebugInfo(`Session ${sessionId} not found in any of ${eventsSnap.docs.length} live events`);
      } catch (error) {
        console.error('[Session] Error during search:', error);
        clearTimeout(timeoutId);
        setNotFound(true);
        setLoading(false);
        setDebugInfo(`Error: ${error}`);
      }
    };

    findSession();

    return () => clearTimeout(timeoutId);
  }, [sessionId, user, navigate]);

  // Timer countdown
  React.useEffect(() => {
    if (!session || session.status !== 'active' || sessionFrozen) return;

    // If session is paused, don't countdown - show time at pause
    if (session.isPaused) {
      const endsAt = session.endsAt.toDate().getTime();
      const pausedAt = session.pausedAt?.toDate().getTime() || Date.now();
      const remaining = Math.max(0, Math.floor((endsAt - pausedAt) / 1000));
      setTimeRemaining(remaining);
      return;
    }

    const updateTimer = () => {
      const endsAt = session.endsAt.toDate().getTime();
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((endsAt - now) / 1000));
      setTimeRemaining(remaining);

      // Vibrate at 30 seconds
      if (remaining === 30 && 'vibrate' in navigator) {
        navigator.vibrate([200, 100, 200, 100, 200]);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [session, sessionFrozen]);

  // Handle finish confirmation - freeze clock and calculate actual bill
  const handleFinishClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmFinish = () => {
    if (!session || !event) return;

    // Calculate actual minutes from start to now
    const startedAt = session.startedAt.toDate().getTime();
    const now = Date.now();
    const elapsedMinutes = Math.ceil((now - startedAt) / 60000);

    // Use the lesser of elapsed time or booked time
    const finalMinutes = Math.min(elapsedMinutes, session.totalMinutes);
    const finalBill = finalMinutes * event.pricePerMinute;

    setActualMinutes(finalMinutes);
    setActualBillAmount(finalBill);
    setAmountReceived(finalBill.toFixed(0));
    setSessionFrozen(true);
    setShowConfirm(false);
    setShowComplete(true);
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const handleBackFromComplete = () => {
    // Unfreeze and go back to session view
    setSessionFrozen(false);
    setShowComplete(false);
    setAmountReceived('');
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const extendSession = async (minutes: number) => {
    if (!eventId || !sessionId) return;
    try {
      const extendSessionFn = httpsCallable(functions, 'extendSession');
      await extendSessionFn({ eventId, sessionId, addMinutes: minutes });
    } catch (err: any) {
      alert(err.message || 'Failed to extend session');
    }
  };

  const completeSession = async () => {
    if (!eventId || !sessionId || !amountReceived) return;

    const amount = parseFloat(amountReceived);
    if (isNaN(amount) || amount < 0) {
      alert('Please enter a valid amount');
      return;
    }

    setCompleting(true);
    try {
      const completeSessionFn = httpsCallable(functions, 'completeSession');
      await completeSessionFn({
        eventId,
        sessionId,
        amountReceived: amount,
      });
      navigate('/t');
    } catch (err: any) {
      alert(err.message || 'Failed to complete session');
      setCompleting(false);
    }
  };

  const pauseSession = async () => {
    if (!eventId || !sessionId) return;
    setPausing(true);
    try {
      const pauseSessionFn = httpsCallable(functions, 'pauseSession');
      await pauseSessionFn({ eventId, sessionId });
    } catch (err: any) {
      alert(err.message || 'Failed to pause session');
    }
    setPausing(false);
  };

  const resumeSession = async () => {
    if (!eventId || !sessionId) return;
    setResuming(true);
    try {
      const resumeSessionFn = httpsCallable(functions, 'resumeSession');
      await resumeSessionFn({ eventId, sessionId });
    } catch (err: any) {
      alert(err.message || 'Failed to resume session');
    }
    setResuming(false);
  };

  if (loading) {
    return (
      <div className="therapist-session loading">
        <div className="spinner"></div>
        <p>Loading session...</p>
      </div>
    );
  }

  // Report issue function for stuck sessions
  const reportIssue = async () => {
    if (!reportComment.trim()) {
      alert('Please describe the issue');
      return;
    }

    setReportSubmitting(true);
    try {
      // We need to find the event that has this session
      // Try searching through events to find it
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const eventsQuery = query(collection(db, 'events'), where('status', '==', 'live'));
      const eventsSnap = await getDocs(eventsQuery);

      let foundEventId: string | null = null;
      for (const eventDoc of eventsSnap.docs) {
        // Check if session exists in this event
        const sessionDoc = await getDocs(
          query(collection(db, 'events', eventDoc.id, 'sessions'), where('__name__', '==', sessionId))
        );
        if (!sessionDoc.empty) {
          foundEventId = eventDoc.id;
          break;
        }
      }

      if (!foundEventId) {
        // Try with the first live event as fallback
        if (eventsSnap.docs.length > 0) {
          foundEventId = eventsSnap.docs[0].id;
        }
      }

      if (foundEventId) {
        const reportSessionIssueFn = httpsCallable(functions, 'reportSessionIssue');
        await reportSessionIssueFn({
          eventId: foundEventId,
          sessionId,
          comment: reportComment,
          issueType: 'stuck',
        });
        setReportSubmitted(true);
        alert('Issue reported successfully. An admin will review it shortly.');
      } else {
        alert('Could not find the event. Please contact admin directly.');
      }
    } catch (err: any) {
      console.error('Error reporting issue:', err);
      alert(err.message || 'Failed to report issue. Please contact admin directly.');
    } finally {
      setReportSubmitting(false);
    }
  };

  if (!session || !event) {
    return (
      <div className="therapist-session error">
        <h2>{searchTimeout ? 'Session Loading Timeout' : 'Session Not Found'}</h2>
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
          {searchTimeout
            ? 'The session could not be loaded. This may be a temporary network issue or the session may no longer exist.'
            : 'This session was not found in any active event. It may have been completed or the event may no longer be live.'}
        </p>
        {debugInfo && (
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-tertiary)',
            backgroundColor: 'var(--color-bg-secondary)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            wordBreak: 'break-all',
          }}>
            Debug: {debugInfo}
            <br />
            Session ID: {sessionId}
          </p>
        )}

        {/* Report Issue Form */}
        {!reportSubmitted && (
          <div style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.5rem',
          }}>
            {!showReportForm ? (
              <button
                onClick={() => setShowReportForm(true)}
                className="btn btn-warning"
                style={{ width: '100%' }}
              >
                Report This Issue
              </button>
            ) : (
              <div>
                <p style={{ marginBottom: '0.75rem', fontWeight: 600 }}>
                  Describe what happened:
                </p>
                <textarea
                  value={reportComment}
                  onChange={(e) => setReportComment(e.target.value)}
                  placeholder="e.g., Session started but got stuck, payment issue, etc."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-bg)',
                    color: 'var(--color-text)',
                    fontSize: '1rem',
                    marginBottom: '0.75rem',
                    resize: 'vertical',
                  }}
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setShowReportForm(false)}
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={reportIssue}
                    className="btn btn-primary"
                    disabled={reportSubmitting || !reportComment.trim()}
                    style={{ flex: 2 }}
                  >
                    {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {reportSubmitted && (
          <div style={{
            backgroundColor: '#22c55e20',
            border: '1px solid #22c55e',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}>
            <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>✓</span>
            Issue reported. Admin will review shortly.
          </div>
        )}

        <p style={{
          fontSize: '0.875rem',
          color: 'var(--color-text-secondary)',
          marginBottom: '1.5rem',
        }}>
          If this keeps happening, please contact your admin to check your account status.
        </p>
        <button onClick={() => navigate('/t')} className="btn btn-primary">
          Go Home
        </button>
      </div>
    );
  }

  const bookedBillAmount = session.totalMinutes * event.pricePerMinute;
  const displayBillAmount = sessionFrozen ? actualBillAmount : bookedBillAmount;
  const displayMinutes = sessionFrozen ? actualMinutes : session.totalMinutes;
  const isOvertime = timeRemaining === 0 && !sessionFrozen && !session.isPaused;
  const isPaused = session.isPaused || false;

  return (
    <div className={`therapist-session ${isOvertime ? 'overtime' : ''}`}>
      {/* Location */}
      <div className="session-location">
        <span className="table">Table {session.tableNo}</span>
        <span className="seat">Seat {session.seatNo}</span>
      </div>

      {/* Timer */}
      <div className={`session-timer ${sessionFrozen ? 'frozen' : ''} ${isPaused ? 'paused' : ''} ${timeRemaining <= 30 && !sessionFrozen && !isPaused ? 'warning' : ''} ${isOvertime ? 'overtime' : ''}`}>
        <div className="time">{sessionFrozen ? '--:--' : formatTime(timeRemaining)}</div>
        <div className="label">
          {sessionFrozen ? 'STOPPED' : (isPaused ? 'PAUSED' : (isOvertime ? 'OVERTIME' : 'remaining'))}
        </div>
      </div>

      {/* Session Info */}
      <div className="session-info">
        <div className="info-item">
          <span className="label">Duration</span>
          <span className="value">{displayMinutes} min</span>
        </div>
        <div className="info-item">
          <span className="label">Bill</span>
          <span className="value">{event.currency} {displayBillAmount.toFixed(0)}</span>
        </div>
      </div>

      {/* Pause/Resume Button */}
      {!showComplete && !showConfirm && !sessionFrozen && (
        <div style={{ marginBottom: '1rem' }}>
          {isPaused ? (
            <button
              className="btn btn-primary btn-xlarge"
              onClick={resumeSession}
              disabled={resuming}
              style={{
                width: '100%',
                backgroundColor: '#22c55e',
                fontSize: '1.25rem',
                padding: '1rem',
              }}
            >
              {resuming ? 'Resuming...' : 'RESUME SESSION'}
            </button>
          ) : (
            <button
              className="btn btn-secondary btn-large"
              onClick={pauseSession}
              disabled={pausing}
              style={{
                width: '100%',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
              }}
            >
              {pausing ? 'Pausing...' : 'PAUSE (Tournament Break)'}
            </button>
          )}
          {session.totalPausedSeconds && session.totalPausedSeconds > 0 && (
            <p style={{
              textAlign: 'center',
              fontSize: '0.75rem',
              color: 'var(--color-text-secondary)',
              marginTop: '0.5rem',
            }}>
              Total paused: {Math.floor(session.totalPausedSeconds / 60)}m {session.totalPausedSeconds % 60}s
            </p>
          )}
        </div>
      )}

      {/* Extend Buttons */}
      {!showComplete && !showConfirm && !sessionFrozen && !isPaused && (
        <div className="extend-section">
          <p>Extend session:</p>
          <div className="extend-buttons">
            {event.extendMinuteOptions.map((mins) => (
              <button
                key={mins}
                className="btn btn-secondary btn-large"
                onClick={() => extendSession(mins)}
              >
                +{mins} min
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="confirm-section" style={{
          padding: '1.5rem',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '12px',
          textAlign: 'center',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Finish Session?</h3>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
            This will stop the clock and calculate the final bill.
          </p>
          <div className="confirm-actions" style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              className="btn btn-secondary btn-large"
              onClick={handleCancelConfirm}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              className="btn btn-success btn-large"
              onClick={handleConfirmFinish}
              style={{ flex: 1 }}
            >
              Yes, Finish
            </button>
          </div>
        </div>
      )}

      {/* Finish Button - hidden when paused */}
      {!showComplete && !showConfirm && !isPaused && (
        <button
          className="btn btn-success btn-xlarge finish-btn"
          onClick={handleFinishClick}
        >
          FINISH SESSION
        </button>
      )}

      {/* Complete Section - Enter Payment */}
      {showComplete && (
        <div className="complete-section">
          <h3>Complete Session</h3>
          <div className="bill-summary">
            <p>Bill: <strong>{event.currency} {displayBillAmount.toFixed(0)}</strong></p>
            <p className="hint">{displayMinutes} min × {event.currency} {event.pricePerMinute}/min</p>
            {displayMinutes < session.totalMinutes && (
              <p className="hint" style={{ color: '#f59e0b' }}>
                (Ended {session.totalMinutes - displayMinutes} min early)
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Amount Received</label>
            <div className="amount-input-wrapper">
              <span className="currency">{event.currency}</span>
              <input
                type="number"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
                placeholder="0"
                step="1"
                min="0"
                autoFocus
                className="amount-input"
              />
            </div>
          </div>

          {amountReceived && parseFloat(amountReceived) > displayBillAmount && (
            <div className="tip-preview">
              Tip: {event.currency} {(parseFloat(amountReceived) - displayBillAmount).toFixed(0)}
            </div>
          )}

          <div className="complete-actions">
            <button
              className="btn btn-secondary btn-large"
              onClick={handleBackFromComplete}
            >
              Back
            </button>
            <button
              className="btn btn-success btn-large"
              onClick={completeSession}
              disabled={completing || !amountReceived}
            >
              {completing ? 'Completing...' : 'Confirm'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
